import { useState, useCallback } from 'react';
import { PendienteMatching, SugerenciaMatching } from '../types';
import { CatalogoService } from '../services/catalogoService';

interface UseMatchingState {
  pendientes: PendienteMatching[];
  sugerencias: SugerenciaMatching[];
  loadingPendientes: boolean;
  loadingSugerencias: boolean;
  loadingConfirmar: boolean;
  error: string | null;
  successMessage: string | null;
}

interface UseMatchingActions {
  cargarPendientes: () => Promise<void>;
  cargarSugerencias: (proveedorProductoId: number) => Promise<void>;
  confirmarMatch: (proveedorProductoId: number, canonicoId: number) => Promise<void>;
  crearCanonicoYMatch: (
    proveedorProductoId: number,
    nombre: string,
    tags: string[],
    subcategoriaId?: number
  ) => Promise<void>;
  limpiarMensajes: () => void;
}

export function useMatching(): UseMatchingState & UseMatchingActions {
  const [state, setState] = useState<UseMatchingState>({
    pendientes: [],
    sugerencias: [],
    loadingPendientes: false,
    loadingSugerencias: false,
    loadingConfirmar: false,
    error: null,
    successMessage: null,
  });

  const cargarPendientes = useCallback(async () => {
    setState((prev) => ({ ...prev, loadingPendientes: true, error: null }));
    try {
      const data = await CatalogoService.listarPendientes();
      setState((prev) => ({ ...prev, pendientes: data, loadingPendientes: false }));
    } catch (err: any) {
      setState((prev) => ({
        ...prev,
        loadingPendientes: false,
        error: err.message || 'Error al cargar pendientes',
      }));
    }
  }, []);

  const cargarSugerencias = useCallback(async (proveedorProductoId: number) => {
    setState((prev) => ({ ...prev, loadingSugerencias: true, error: null, sugerencias: [] }));
    try {
      const data = await CatalogoService.obtenerSugerencias(proveedorProductoId);
      setState((prev) => ({ ...prev, sugerencias: data, loadingSugerencias: false }));
    } catch (err: any) {
      setState((prev) => ({
        ...prev,
        loadingSugerencias: false,
        error: err.message || 'Error al cargar sugerencias',
      }));
    }
  }, []);

  const confirmarMatch = useCallback(
    async (proveedorProductoId: number, canonicoId: number) => {
      setState((prev) => ({ ...prev, loadingConfirmar: true, error: null }));
      try {
        await CatalogoService.confirmarMatch(proveedorProductoId, canonicoId);
        setState((prev) => ({
          ...prev,
          loadingConfirmar: false,
          successMessage: 'Match confirmado exitosamente',
          pendientes: prev.pendientes.map((p) =>
            p.pendientes > 0 ? { ...p, pendientes: p.pendientes - 1 } : p
          ),
        }));
      } catch (err: any) {
        setState((prev) => ({
          ...prev,
          loadingConfirmar: false,
          error: err.message || 'Error al confirmar match',
        }));
      }
    },
    []
  );

  const crearCanonicoYMatch = useCallback(
    async (
      proveedorProductoId: number,
      nombre: string,
      tags: string[],
      subcategoriaId?: number
    ) => {
      setState((prev) => ({ ...prev, loadingConfirmar: true, error: null }));
      try {
        await CatalogoService.crearCanonicoYMatch(proveedorProductoId, {
          nombre,
          tags,
          subcategoriaId,
        });
        setState((prev) => ({
          ...prev,
          loadingConfirmar: false,
          successMessage: 'Canónico creado y match confirmado',
        }));
      } catch (err: any) {
        setState((prev) => ({
          ...prev,
          loadingConfirmar: false,
          error: err.message || 'Error al crear canónico',
        }));
      }
    },
    []
  );

  const limpiarMensajes = useCallback(() => {
    setState((prev) => ({ ...prev, error: null, successMessage: null }));
  }, []);

  return {
    ...state,
    cargarPendientes,
    cargarSugerencias,
    confirmarMatch,
    crearCanonicoYMatch,
    limpiarMensajes,
  };
}
